import { useState, useCallback } from "../imports";
import { useClientPrincipal } from "../context/ClientPrincipalContext";
import { useNotification } from "../context/NotificationContext";
import { ShortenedUrl, ShortenedUrlData } from "../types/urlShortener.types";

const useShortenedUrls = () => {
  const [shortenedUrls, setShortenedUrls] = useState<
    Record<string, ShortenedUrl>
  >({});
  const [error, setError] = useState<string>("");
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const { clientPrincipal } = useClientPrincipal();
  const { setNotification } = useNotification();

  const fetchShortenedUrls = useCallback(
    async (userIdOverride?: string) => {
      const userId = userIdOverride || clientPrincipal?.userId;
      if (!userId) {
        return;
      }

      try {
        const response = await fetch(`${backendUrl}/urls`, {
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "client-principal-user-id": userId,
          },
        });

        if (!response.ok) throw new Error("Failed to fetch shortened URLs");

        const data: ShortenedUrlData[] = await response.json();

        let statsData: {
          urls: Array<{ shortCode: string; totalScans: number }>;
        } = { urls: [] };

        try {
          const statsResponse = await fetch(`${backendUrl}/stats`, {
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              "client-principal-user-id": userId,
            },
          });

          if (statsResponse.ok) {
            statsData = await statsResponse.json();
          }
        } catch (statsError) {}

        const entries: [string, ShortenedUrl][] = data.map(
          ({
            short_code,
            original_url,
            name,
            prefix,
            isfavourite,
            fileFormat,
            expiration_date,
            custom_color,
            with_logo,
            logo_id,
          }) => {
            const urlStats = statsData.urls.find(
              (s) => s.shortCode === short_code,
            );

            return [
              short_code,
              {
                url: original_url,
                name,
                prefix,
                isFavourite: isfavourite,
                fileFormat: fileFormat || "png",
                expirationDate: expiration_date ?? "",
                customColor: custom_color || "#000000",
                withLogo: with_logo ?? true,
                logoId: logo_id || "cgi",
                totalScans: urlStats?.totalScans || 0,
              },
            ];
          },
        );

        entries.sort(([, a], [, b]) => {
          if (a.isFavourite === b.isFavourite) return 0;
          return a.isFavourite ? -1 : 1;
        });

        const urlsWithStatus = Object.fromEntries(entries);

        setShortenedUrls(urlsWithStatus);
      } catch (error) {
        setError("Failed to fetch shortened URLs");
        setNotification({
          type: "error",
          message: "Failed to load URLs. Please try again later.",
        });
      }
    },
    [backendUrl, clientPrincipal, setNotification],
  );

  const updateFileFormat = (shortCode: string, format: "png" | "svg") => {
    setShortenedUrls((prev) => ({
      ...prev,
      [shortCode]: {
        ...prev[shortCode],
        fileFormat: format,
      },
    }));
  };

  return {
    shortenedUrls,
    error,
    setShortenedUrls,
    fetchShortenedUrls,
    updateFileFormat,
  };
};

export default useShortenedUrls;
